import { WarningFilled } from '@ant-design/icons'
import { Horizontal, NothingMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Radio } from 'antd'
import { useEffect } from 'react'

import { useDestinationsContext } from '../../Contexts/DestinationsContext'
import { useOrderContainersContext } from '../../Contexts/OrderContainersContext'
import { useSupplyOptionContext } from '../../Contexts/SupplyOptionContext'
import { useWizardMaster } from '../../Contexts/WizardMasterContext'
import { SupplyLoadingAnim } from './DisplayComponents/SupplyOptions/SupplyLoadingAnim'
import { SupplyOptionCard } from './DisplayComponents/SupplyOptions/SupplyOptionCard'
import { allSupplyOptionsOnOrder } from './util'

export function SupplyOptionSelect() {
  const { wizardOrder, currentEntryMode } = useWizardMaster()
  const {
    supplyOptions,
    reloadSupplyOptions,
    selectedSort,
    setSelectedSort,
    selectedSupply,
    includeAlternates: use_alternates,
    onlyInNetwork: only_in_network,
    onlySingleSupplier: single_supplier_per_load,
    excludeOutages: exclude_outages,
    bestSupplyHash,
    optionsQueryStatus,
  } = useSupplyOptionContext()
  const { orderDestinations } = useDestinationsContext()
  const { compartments, haveCompartmentsChanged, productGroupsApi } = useOrderContainersContext()

  const { hasStoreChanged, hasProductChanged, hasVolumeChanged } = haveCompartmentsChanged(
    wizardOrder && wizardOrder.compartments
  )

  const needsReload = hasStoreChanged || hasProductChanged || hasVolumeChanged || !supplyOptions
  const isByProduct = currentEntryMode === 'by_product'

  useEffect(() => {
    const useExistingSupplyOwners = allSupplyOptionsOnOrder(
      wizardOrder,
      compartments,
      productGroupsApi.productGroups,
      isByProduct
    )
    if (needsReload) {
      const supplyOrder = wizardOrder || null
      const supplyDestinations = orderDestinations.map((o) => o.store_number)
      setSelectedSort('Best')
      reloadSupplyOptions(
        supplyOrder,
        isByProduct ? productGroupsApi.destinations : supplyDestinations,
        isByProduct ? productGroupsApi.compartments : compartments,
        'Best',
        needsReload,
        undefined,
        { use_alternates, only_in_network, single_supplier_per_load, exclude_outages },
        useExistingSupplyOwners ? wizardOrder.supply_option.supply_owner_map : undefined,
        productGroupsApi.productGroups,
        isByProduct
      )
    }
  }, [compartments])

  if (optionsQueryStatus === 'pending' || optionsQueryStatus === 'fetching') {
    return <SupplyLoadingAnim />
  }

  if (supplyOptions?.length === 0) {
    return (
      <SupplyOptionSortHeader needsReload={needsReload}>
        <NothingMessage
          title='No Results'
          message='No supply options were found for that destination/product combination.'
        />
      </SupplyOptionSortHeader>
    )
  }

  if (optionsQueryStatus === 'error') {
    return (
      <Vertical fullHeight flex={1} verticalCenter horizontalCenter className='p-3'>
        <Texto className='mb-2' category='h2' appearance='warning'>
          <WarningFilled className='pr-3' />
          Supply Options Failed.
        </Texto>
        <Texto category='h4'>Please refresh and try again. </Texto>
      </Vertical>
    )
  }

  if (supplyOptions) {
    return (
      <SupplyOptionSortHeader needsReload={needsReload} selectedSupply={selectedSupply}>
        {supplyOptions?.[0]?.default_supply ? (
          <BestBuyOption option={supplyOptions[0]} bestSupplyHash={bestSupplyHash} />
        ) : (
          selectedSort === 'Best' && <BestBuyOption option={supplyOptions[0]} bestSupplyHash={bestSupplyHash} />
        )}
        <div className='option-grouping-header mt-4 mb-3'>{selectedSort === 'Best' ? 'Other Options' : ''}</div>
        {supplyOptions
          .map((option, index) => (!index ? { ...option, isBest: true } : option))
          .map((supply, index) => {
            if (supply.isBest && selectedSort === 'Best') return null
            return (
              <SupplyOptionCard key={index} isBest={selectedSort === 'Shortest' && index === 0} supplyOption={supply} />
            )
          })}
      </SupplyOptionSortHeader>
    )
  }
  return null
}

function BestBuyOption({ option, bestSupplyHash }) {
  return (
    <>
      <Texto category='h5' className='mb-3'>
        Best Buy Recommends
      </Texto>
      <SupplyOptionCard isBest={option.hash === bestSupplyHash} supplyOption={option} />
    </>
  )
}

function SupplySortBy({ needsReload }) {
  const {
    reloadSupplyOptions,
    selectedSort,
    includeAlternates: use_alternates,
    onlyInNetwork: only_in_network,
    onlySingleSupplier: single_supplier_per_load,
    excludeOutages: exclude_outages,
  } = useSupplyOptionContext()
  const { orderDestinations } = useDestinationsContext()
  const { compartments, productGroupsApi } = useOrderContainersContext()
  const { wizardOrder, orderEdited, currentEntryMode } = useWizardMaster()
  const isByProduct = currentEntryMode === 'by_product'
  const order = orderEdited ? null : wizardOrder
  const supplyDestinations = orderDestinations.map((o) => o.store_number)

  return (
    <Horizontal verticalCenter>
      <Horizontal className='mr-3' verticalCenter>
        <Texto className='pr-2' weight={600} style={{ whiteSpace: 'nowrap' }}>
          SORT BY
        </Texto>
        <Horizontal>
          <Radio.Group
            options={['Best', 'Shortest']}
            onChange={({ target: { value } }) =>
              reloadSupplyOptions(
                order,
                isByProduct ? productGroupsApi.destinations : supplyDestinations,
                isByProduct ? productGroupsApi.compartments : compartments,
                value,
                needsReload,
                undefined,
                { use_alternates, only_in_network, single_supplier_per_load, exclude_outages },
                undefined,
                productGroupsApi.productGroups,
                isByProduct
              )
            }
            value={selectedSort}
            optionType='button'
            buttonStyle='solid'
            size='small'
          />
        </Horizontal>
      </Horizontal>
    </Horizontal>
  )
}

function SupplyOptionSortHeader({ needsReload, selectedSupply, children }) {
  return (
    <>
      <Horizontal
        justifyContent='flex-end'
        className='px-3 py-2'
        bg='elevated'
        border='all'
        style={{ overflowX: 'auto' }}
      >
        <SupplySortBy needsReload={needsReload} />
      </Horizontal>
      <div className='supply-card-option-container flex-wrap items-center p-3'>
        {selectedSupply && (
          <div>
            <Texto category='h5' className='mb-3'>
              Currently Selected
            </Texto>
            <SupplyOptionCard selected isBest supplyOption={selectedSupply} />
          </div>
        )}
        {children}
      </div>
    </>
  )
}
